import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

interface HighchartsLike {
  chart: (renderTo: HTMLElement, options: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    Highcharts?: HighchartsLike;
  }
}

export const TeamPerformanceOverview = () => {
  const utilizationGaugeRef = useRef<HTMLDivElement>(null);
  
  const { data: metrics, isLoading } = useQuery({
    queryKey: ['team-performance'],
    queryFn: async () => {
      // Get transactions with AI classifications
      const { data: transactions, error: txError } = await supabase
        .from('transactions')
        .select('id, ai_classification_confidence, human_verified, created_at');

      if (txError) throw txError;

      const total = transactions?.length || 0;
      const verified = transactions?.filter(t => t.human_verified).length || 0;
      const avgConfidence = total > 0
        ? transactions.reduce((sum, t) => sum + (t.ai_classification_confidence || 0), 0) / total
        : 0;

      // Calculate processing volume (last 7 days)
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const recentTxs = transactions?.filter(t => 
        new Date(t.created_at) >= sevenDaysAgo
      ).length || 0;

      return {
        utilization: Math.round((verified / Math.max(total, 1)) * 100),
        accuracy: avgConfidence.toFixed(1),
        volumePerDay: Math.round(recentTxs / 7),
        satisfaction: 4.9 // This would come from user ratings if implemented
      };
    },
    refetchInterval: 60000
  });

  useEffect(() => {
    if (!utilizationGaugeRef.current || typeof window === 'undefined') {
      return;
    }

    const highcharts = window.Highcharts;
    if (!highcharts) {
      return;
    }

    const options: Record<string, unknown> = {
        chart: { 
          type: 'solidgauge', 
          backgroundColor: 'transparent', 
          margin: [0,0,0,0] 
        },
        title: null,
        pane: {
          center: ['50%', '50%'],
          size: '100%',
          startAngle: 0,
          endAngle: 360,
          background: {
            backgroundColor: '#ecf4e9',
            innerRadius: '80%',
            outerRadius: '100%',
            shape: 'arc',
            borderWidth: 0
          }
        },
        tooltip: { enabled: false },
        yAxis: { 
          min: 0, 
          max: 100, 
          lineWidth: 0, 
          tickPositions: [] 
        },
        plotOptions: { 
          solidgauge: { 
            dataLabels: { enabled: false }, 
            innerRadius: '80%' 
          } 
        },
        series: [{
          name: 'Utilization',
          data: [{
            color: '#1e4841',
            radius: '100%',
            innerRadius: '80%',
            y: 88
          }]
        }],
        credits: { enabled: false },
      };

      highcharts.chart(utilizationGaugeRef.current, options);
  }, []);

  if (isLoading) {
    return (
      <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-header-mb">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="p-4 bg-white rounded-2xl border border-brand-border">
            <Skeleton className="h-16" />
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-header-mb">
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="w-12 h-12 flex-shrink-0">
          <div ref={utilizationGaugeRef}></div>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Avg. Team Utilization</p>
          <p className="text-xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.utilization || 0}%</p>
        </div>
      </div>
      
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="w-12 h-12 flex-shrink-0 bg-brand-secondary rounded-full flex items-center justify-center">
          <i className="fa-solid fa-bullseye text-2xl text-brand-primary"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Team Accuracy</p>
          <p className="text-xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.accuracy || 0}%</p>
        </div>
      </div>
      
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="w-12 h-12 flex-shrink-0 bg-brand-secondary rounded-full flex items-center justify-center">
          <i className="fa-solid fa-layer-group text-2xl text-brand-primary"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Processing Volume</p>
          <p className="text-xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.volumePerDay || 0}/day</p>
        </div>
      </div>
      
      <div className="p-4 bg-white rounded-2xl border border-brand-border flex items-center gap-4">
        <div className="w-12 h-12 flex-shrink-0 bg-brand-secondary rounded-full flex items-center justify-center">
          <i className="fa-solid fa-face-smile text-2xl text-brand-primary"></i>
        </div>
        <div>
          <p className="text-xs text-brand-text-secondary">Client Satisfaction</p>
          <p className="text-xl text-brand-text-primary font-space-grotesk font-bold">{metrics?.satisfaction || 0}/5</p>
        </div>
      </div>
    </section>
  );
};
